import { memo, useEffect, useState } from "react";
import type { ReactNode } from "react";
import FuseSvgIcon from "@fuse/core/FuseSvgIcon";

type BannerSlide = {
	tag: string;
	title: ReactNode;
	description: string;
	pill: string;
	illustration: ReactNode;
};

const INTERVAL_MS = 4500;

const DASHBOARD_BARS = [
	{ height: "h-[38px]", color: "bg-[#504a99]/50" },
	{ height: "h-[28px]", color: "bg-[#504a99]/40" },
	{ height: "h-[45px]", color: "bg-[#e6215a]/60" },
	{ height: "h-[34px]", color: "bg-[#504a99]/50" },
	{ height: "h-[42px]", color: "bg-[#76b0bf]/50" },
	{ height: "h-[48px]", color: "bg-[#e6215a]/70" },
	{ height: "h-[40px]", color: "bg-[#504a99]/50" },
	{ height: "h-[52px]", color: "bg-[#76b0bf]/60" },
];

function DashboardIllustration() {
	return (
		<div className="absolute top-5 left-1/2 flex h-60 w-[260px] -translate-x-1/2 items-center justify-center group-data-[densidade=compacta]/login:h-40">
			<div className="h-[155px] w-[220px] rounded-[10px] border border-[#504a99]/30 bg-[#504a99]/15 p-3">
				<div className="mb-2 h-2.5 w-15 rounded bg-[#76b0bf]/40" />
				<div className="mb-4 grid h-[55px] grid-cols-2 gap-2 rounded-md border border-[#504a99]/25 bg-[#504a99]/20 p-2">
					<div>
						<div className="mb-2 h-1.5 w-10 rounded bg-white/30" />
						<div className="flex h-5 items-center justify-center rounded bg-[#e6215a]/25 text-[12px] font-bold text-white/80">
							R$ 148k
						</div>
					</div>
					<div>
						<div className="mb-2 h-1.5 w-10 rounded bg-white/30" />
						<div className="flex h-5 items-center justify-center rounded bg-[#76b0bf]/25 text-[12px] font-bold text-white/80">
							1.204
						</div>
					</div>
				</div>
				<div className="flex h-[58px] items-end gap-1.5 px-1">
					{DASHBOARD_BARS.map((bar, index) => (
						<div
							key={`${bar.height}-${index}`}
							className={`w-3.5 rounded-[3px] ${bar.height} ${bar.color}`}
						/>
					))}
				</div>
			</div>
		</div>
	);
}

function MarketplaceIllustration() {
	const nodeClass =
		"absolute flex h-9 w-9 items-center justify-center rounded-full border text-[11px] font-medium text-white/70";

	return (
		<div className="absolute top-5 left-1/2 flex h-60 w-[260px] -translate-x-1/2 items-center justify-center group-data-[densidade=compacta]/login:h-40">
			<div className="relative h-[150px] w-[150px]">
				<div className="absolute inset-x-[52px] top-[35px] h-[80px] w-px border-l border-dashed border-white/15" />
				<div className="absolute inset-y-[52px] left-[35px] h-px w-[80px] border-t border-dashed border-white/15" />
				<div className="absolute top-1/2 left-1/2 flex h-[90px] w-[90px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-[#504a99]/30 bg-[#504a99]/15">
					<FuseSvgIcon size={34} className="text-white/80">
						lucide:shopping-cart
					</FuseSvgIcon>
				</div>
				<div className={`${nodeClass} top-0 left-1/2 -translate-x-1/2 border-[#e6215a]/40 bg-[#e6215a]/20`}>
					ML
				</div>
				<div className={`${nodeClass} top-1/2 right-0 -translate-y-1/2 border-[#76b0bf]/40 bg-[#76b0bf]/20`}>
					Shp
				</div>
				<div className={`${nodeClass} top-1/2 left-0 -translate-y-1/2 border-[#504a99]/40 bg-[#504a99]/25`}>
					Tray
				</div>
				<div className={`${nodeClass} bottom-0 left-1/2 -translate-x-1/2 border-[#67a0d6]/40 bg-[#67a0d6]/20`}>
					VTX
				</div>
			</div>
		</div>
	);
}

function InventoryIllustration() {
	return (
		<div className="absolute top-5 left-1/2 flex h-60 w-[260px] -translate-x-1/2 items-center justify-center group-data-[densidade=compacta]/login:h-40">
			<div className="grid w-[180px] grid-cols-[80px_80px] gap-5">
				<div className="row-span-2 rounded-lg border border-[#504a99]/30 bg-[#504a99]/20 p-2">
					<div className="mb-2 flex h-[50px] items-center justify-center rounded bg-white/[0.06]">
						<FuseSvgIcon size={30} className="text-white/75">
							lucide:package
						</FuseSvgIcon>
					</div>
					<div className="mb-2 h-1.5 w-10 rounded bg-white/30" />
					<div className="h-1.5 w-6 rounded bg-[#e6215a]/50" />
				</div>

				<div className="rounded-lg border border-[#76b0bf]/30 bg-[#76b0bf]/15 p-2">
					<div className="mb-2 h-1.5 w-7 rounded bg-white/25" />
					<div className="flex items-end gap-1">
						<div className="h-4 w-3 rounded-sm bg-[#76b0bf]/40" />
						<div className="h-3 w-3 rounded-sm bg-[#504a99]/50" />
						<div className="h-5 w-3 rounded-sm bg-[#e6215a]/50" />
					</div>
				</div>

				<div className="rounded-lg border border-[#67a0d6]/30 bg-[#67a0d6]/15 p-2">
					<div className="flex h-7 items-center justify-center">
						<FuseSvgIcon size={26} className="text-white/75">
							lucide:receipt-text
						</FuseSvgIcon>
					</div>
					<div className="mt-2 h-1 w-full rounded bg-[#67a0d6]/50" />
				</div>
			</div>
		</div>
	);
}

const SLIDE_CONFIG: BannerSlide[] = [
	{
		tag: "Dashboard",
		title: (
			<>
				Visão completa do
				<br />
				seu <em className="not-italic text-[#e6215a]">desempenho</em>
			</>
		),
		description: "Acompanhe vendas, estoque e metas em tempo real com painéis intuitivos e inteligentes.",
		pill: "Dados em tempo real",
		illustration: <DashboardIllustration />,
	},
	{
		tag: "Marketplaces",
		title: (
			<>
				Venda em todos os
				<br />
				<em className="not-italic text-[#e6215a]">canais</em> ao mesmo tempo
			</>
		),
		description: "Integração nativa com Mercado Livre, Shopee, VTEX, Tray e mais. Estoque e pedidos sincronizados.",
		pill: "+5 integrações ativas",
		illustration: <MarketplaceIllustration />,
	},
	{
		tag: "Gestão de Estoque",
		title: (
			<>
				Controle total de
				<br />
				produtos e <em className="not-italic text-[#e6215a]">grades</em>
			</>
		),
		description: "Gerencie grades de tamanho e cor, movimentações, notas fiscais e transferências entre filiais.",
		pill: "NF-e integrada com Receita",
		illustration: <InventoryIllustration />,
	},
];

// ------------------------------------------------------- main
function SignInPageSlideSection() {
	const [active, setActive] = useState(0);

	useEffect(() => {
		const timer = globalThis.setInterval(() => {
			setActive((prev) => (prev + 1) % SLIDE_CONFIG.length);
		}, INTERVAL_MS);

		return () => globalThis.clearInterval(timer);
	}, []);

	return (
		<aside className="relative hidden flex-1 overflow-hidden bg-[linear-gradient(140deg,#040d1a_0%,#071428_50%,#0a1a36_100%)] text-white md:flex md:flex-col">
			<div
				className="pointer-events-none absolute inset-0"
				style={{
					backgroundImage:
						"linear-gradient(rgba(80,74,153,.07) 1px, transparent 1px), linear-gradient(90deg, rgba(80,74,153,.07) 1px, transparent 1px)",
					backgroundSize: "40px 40px",
					maskImage: "radial-gradient(ellipse 80% 80% at 50% 50%, black, transparent)",
					WebkitMaskImage: "radial-gradient(ellipse 80% 80% at 50% 50%, black, transparent)",
				}}
			/>
			<div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_80%_55%_at_82%_6%,rgba(80,74,153,.34),transparent_58%),radial-gradient(ellipse_55%_45%_at_10%_90%,rgba(118,176,191,.24),transparent_62%),radial-gradient(ellipse_45%_35%_at_78%_76%,rgba(230,33,90,.14),transparent_70%)]" />

			<div className="relative flex-1">
				{SLIDE_CONFIG.map((slide, index) => (
					<div
						key={slide.tag}
						className={`absolute inset-0 flex flex-col justify-end px-10 pt-10 pb-16 transition-opacity duration-700 ease-in-out group-data-[densidade=compacta]/login:px-8 group-data-[densidade=compacta]/login:pt-6 group-data-[densidade=compacta]/login:pb-12 ${
							index === active ? "opacity-100" : "pointer-events-none opacity-0"
						}`}
					>
						{slide.illustration}
						<div className="mb-2.5 inline-flex items-center gap-1.5 text-[12px] font-bold tracking-[0.1em] text-[#76b0bf] uppercase">
							<span className="h-0.5 w-[32px] rounded bg-[#76b0bf]" />
							{slide.tag}
						</div>
						<h2 className="mb-2.5 text-[40px] leading-[1.2] font-bold text-white drop-shadow-[0_2px_12px_rgba(0,0,0,.6)] group-data-[densidade=compacta]/login:text-[32px]">
							{slide.title}
						</h2>
						<p className="mb-5 max-w-80 text-[13px] leading-[1.6] text-white/60">{slide.description}</p>
						<div className="inline-flex w-fit items-center gap-1.5 rounded-full border border-white/[0.12] bg-white/[0.06] px-3.5 py-1.5 text-[11px] font-medium text-white/75 backdrop-blur">
							<span className="h-1.5 w-1.5 rounded-full bg-[#76b0bf]" />
							{slide.pill}
						</div>
					</div>
				))}
			</div>

			<div className="absolute right-0 bottom-0 left-0 z-20 flex items-center justify-center gap-2 p-4">
				{SLIDE_CONFIG.map((slide, index) => (
					<button
						key={slide.tag}
						type="button"
						aria-label={`Mostrar slide ${index + 1}`}
						onClick={() => setActive(index)}
						className={`h-2 rounded-[6px] border-0 transition-[width,background-color] duration-300 hover:cursor-pointer hover:scale-125 ${
							index === active ? "w-5 bg-[#e6215a]" : "w-3 bg-white/25"
						}`}
					/>
				))}
			</div>
		</aside>
	);
}

export default memo(SignInPageSlideSection);
